// src/components/InvoiceForm.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Paper,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Autocomplete,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Box,
  Divider,
} from "@mui/material";
import SollCalculator from "./calculatorButton"; // Ensure the correct path.
import { getToken, base_url } from "../../../config/config";

const InvoiceForm = ({ onInvoiceCreated }) => {
  const [invoiceData, setInvoiceData] = useState({
    Konto: "",
    Datum: "",
    Beleg1: "",
    AN: "",
    BN: "",
    Gkto: "",
    Buchungstext: "",
    BU: "",
    Soll: "",
    Haben: "",
    Kost1: "",
    Kost2: "",
    isLieferant: false,
    lieferantId: "",
    lieferantName: "",
    lieferantKonto: "",
    kundeId: "",
  });

  // Kunde (Customer) data
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Lieferant (Supplier) data
  const [lieferanten, setLieferanten] = useState([]);
  const [selectedLieferant, setSelectedLieferant] = useState(null);
  const [loadingLieferanten, setLoadingLieferanten] = useState(false);
  
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState(null);

  // Fetch companies and lieferanten on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch companies (kunde)
        setLoadingCompanies(true);
        const companyResponse = await axios.get(`${base_url}/v1/getFirma`);
        setCompanies(companyResponse.data.data);
        setLoadingCompanies(false);
        
        // Fetch lieferanten (suppliers)
        setLoadingLieferanten(true);
        const lieferantResponse = await axios.get(`${base_url}/v1/getLieferant`);
        setLieferanten(lieferantResponse.data.data);
        setLoadingLieferanten(false);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch companies or suppliers");
        setLoadingCompanies(false);
        setLoadingLieferanten(false);
      }
    };

    fetchData();
  }, []);

  // Handle changes in form fields (excluding Soll)
  const handleChange = (e) => {
    const { name, value } = e.target;

    // If Konto is typed manually, attempt to find matching company
    if (name === "Konto") {
      const matchedCompany = companies.find(
        (company) => company.konto === value,
      );
      if (matchedCompany) {
        setSelectedCompany(matchedCompany);
        setInvoiceData({
          ...invoiceData,
          Konto: matchedCompany.konto,
          AN: matchedCompany.bezeichnung,
          Buchungstext: matchedCompany.bezeichnung,
          // Add other fields if necessary
        });
        return;
      } else {
        setSelectedCompany(null);
      }
    }

    setInvoiceData({
      ...invoiceData,
      [name]: value,
    });
  };

  // Handle invoice type change (kunde/lieferant)
  const handleInvoiceTypeChange = (event) => {
    const isLieferant = event.target.value === 'lieferant';
    
    // Reset selection and data when switching types
    setSelectedCompany(null);
    setSelectedLieferant(null);
    
    setInvoiceData({
      ...invoiceData,
      isLieferant,
      Konto: "",
      AN: "",
      Buchungstext: "",
      lieferantId: "",
      lieferantName: "",
      lieferantKonto: "",
      kundeId: ""
    });
  };

  // Handle company (kunde) selection from autocomplete
  const handleCompanySelect = (event, value) => {
    if (value) {
      setSelectedCompany(value);
      setSelectedLieferant(null);
      setInvoiceData({
        ...invoiceData,
        Konto: value.konto,
        AN: value.bezeichnung,
        Buchungstext: value.bezeichnung,
        isLieferant: false,
        kundeId: value._id,
        lieferantId: "",
        lieferantName: "",
        lieferantKonto: ""
      });
    } else {
      setSelectedCompany(null);
      setInvoiceData({
        ...invoiceData,
        Konto: "",
        AN: "",
        Buchungstext: "",
        kundeId: ""
      });
    }
  };
  
  // Handle lieferant (supplier) selection from autocomplete
  const handleLieferantSelect = (event, value) => {
    if (value) {
      setSelectedLieferant(value);
      setSelectedCompany(null);
      setInvoiceData({
        ...invoiceData,
        Konto: value.konto,
        AN: value.bezeichnung,
        Buchungstext: value.bezeichnung,
        isLieferant: true,
        lieferantId: value._id,
        lieferantName: value.bezeichnung,
        lieferantKonto: value.konto,
        kundeId: null
      });
    } else {
      setSelectedLieferant(null);
      setInvoiceData({
        ...invoiceData,
        Konto: "",
        AN: "",
        Buchungstext: "",
        lieferantId: "",
        lieferantName: "",
        lieferantKonto: ""
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    setError(null);

    // Validate that Soll is a number
    const sollValue = Number(invoiceData.Soll);
    if (isNaN(sollValue)) {
      setError("Der Soll-Wert muss eine gültige Zahl sein.");
      setLoadingSubmit(false);
      return;
    }

    // Prepare the data with proper lieferant/kunde distinction
    const dataToSend = {
      ...invoiceData,
      Soll: sollValue, // Ensure Soll is a number
      isLieferant: invoiceData.isLieferant,
      lieferantId: invoiceData.lieferantId,
      lieferantName: invoiceData.lieferantName,
      lieferantKonto: invoiceData.lieferantKonto,
      kundeId: invoiceData.kundeId
    };

    console.log("Submitting Invoice Data:", dataToSend); // Debugging line

    try {
      const response = await axios.post(`${base_url}/v1/uploadRechnungs`, [
        dataToSend,
      ]);
      alert(response.data.message);
      onInvoiceCreated(response.data.invoice); // Pass the created invoice back to parent

      // Reset form
      setInvoiceData({
        Konto: "",
        Datum: "",
        Beleg1: "",
        AN: "",
        BN: "",
        Gkto: "",
        Buchungstext: "",
        BU: "",
        Soll: "",
        Haben: "",
        Kost1: "",
        Kost2: "",
        isLieferant: false,
        lieferantId: "",
        lieferantName: "",
        lieferantKonto: "",
        kundeId: ""
      });
      setSelectedCompany(null);
      setSelectedLieferant(null);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to upload invoice");
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Handle changes in the Soll field via SollCalculator
  const handleSollChange = (value, isManualChange = true) => {
    if (isManualChange) {
      // If the user is typing manually, update the Soll field without evaluating
      setInvoiceData({
        ...invoiceData,
        Soll: value,
      });
    } else {
      // If the Plus (+) button was pressed, set the evaluated total
      setInvoiceData({
        ...invoiceData,
        Soll: value,
      });
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 4, marginBottom: 4 }}>
      <Typography variant='h5' gutterBottom>
        Create New Invoice
      </Typography>
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Invoice Type Selection (Kunde or Lieferant) */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle1" gutterBottom>
                Rechnungstyp
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="invoiceType"
                  value={invoiceData.isLieferant ? 'lieferant' : 'kunde'}
                  onChange={handleInvoiceTypeChange}
                >
                  <FormControlLabel 
                    value="kunde" 
                    control={<Radio />} 
                    label="Kunde (Eingehende Rechnung)" 
                  />
                  <FormControlLabel 
                    value="lieferant" 
                    control={<Radio />} 
                    label="Lieferant (Ausgehende Rechnung)" 
                  />
                </RadioGroup>
              </FormControl>
            </Paper>
          </Grid>
          
          {/* Kunde (Customer) Selection - shown when isLieferant is false */}
          {!invoiceData.isLieferant && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Kundenauswahl
              </Typography>
              {loadingCompanies ? (
                <CircularProgress />
              ) : (
                <Autocomplete
                  options={companies}
                  getOptionLabel={(option) =>
                    `${option.bezeichnung} (${option.konto})`
                  }
                  onChange={handleCompanySelect}
                  value={selectedCompany}
                  renderInput={(params) => (
                    <TextField {...params} label='Firma' variant='outlined' />
                  )}
                  freeSolo
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase();
                    return options.filter(
                      (option) =>
                        option.konto.includes(inputValue) ||
                        option.bezeichnung.toLowerCase().includes(inputValue) ||
                        option.kurzbezeichnung.toLowerCase().includes(inputValue),
                    );
                  }}
                />
              )}
            </Grid>
          )}
          
          {/* Lieferant (Supplier) Selection - shown when isLieferant is true */}
          {invoiceData.isLieferant && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Lieferantenauswahl
              </Typography>
              {loadingLieferanten ? (
                <CircularProgress />
              ) : (
                <Autocomplete
                  options={lieferanten}
                  getOptionLabel={(option) =>
                    `${option.bezeichnung} (${option.konto})`
                  }
                  onChange={handleLieferantSelect}
                  value={selectedLieferant}
                  renderInput={(params) => (
                    <TextField {...params} label='Lieferant' variant='outlined' />
                  )}
                  freeSolo
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase();
                    return options.filter(
                      (option) =>
                        option.konto.includes(inputValue) ||
                        option.bezeichnung.toLowerCase().includes(inputValue) ||
                        (option.kurzbezeichnung && option.kurzbezeichnung.toLowerCase().includes(inputValue)),
                    );
                  }}
                />
              )}
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Konto Field */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Konto'
              name='Konto'
              value={invoiceData.Konto}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          {/* AN (Company Name) Field */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='AN (Firma)'
              name='AN'
              value={invoiceData.AN}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          {/* Datum */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Datum'
              name='Datum'
              type='date'
              value={invoiceData.Datum}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          {/* Beleg1 */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Beleg1'
              name='Beleg1'
              value={invoiceData.Beleg1}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          {/* BN */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='BN'
              name='BN'
              value={invoiceData.BN}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Gkto */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Gkto'
              name='Gkto'
              value={invoiceData.Gkto}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Buchungstext */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Buchungstext'
              name='Buchungstext'
              type='text'
              value={invoiceData.Buchungstext}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* BU */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='BU'
              name='BU'
              value={invoiceData.BU}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Soll with Automatic Calculator */}
          <Grid item xs={12} sm={6}>
            <SollCalculator
              value={invoiceData.Soll}
              onCalculate={handleSollChange}
            />
          </Grid>

          {/* Haben */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Haben'
              name='Haben'
              value={invoiceData.Haben}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Kost1 */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Kost1'
              name='Kost1'
              value={invoiceData.Kost1}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Kost2 */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Kost2'
              name='Kost2'
              value={invoiceData.Kost2}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              disabled={loadingSubmit}
              startIcon={loadingSubmit && <CircularProgress size={20} />}>
              {loadingSubmit ? "Uploading..." : "Create Invoice"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default InvoiceForm;
